/**
 * useUnitConversion - Composable for Imperial/Metric unit conversion
 *
 * Provides reactive unit conversion based on user's temperature unit preference.
 * All weather data is stored in Imperial units (°F, mph, in) and converted
 * to Metric (°C, kmh, mm) when the user preference is set to Metric.
 */

import { computed } from "vue";
import {
  fahrenheitToCelsius,
  milesPerHourToKilometersPerHour,
  inchesToMillimeters,
  milesToKilometers,
  feetToMeters,
  roundToPlaces,
} from "rainowls-shared";
import {
  useSettingsStore,
  type TemperatureUnit,
} from "@/stores/settings.store";

/**
 * Unit labels for display
 */
export interface UnitLabels {
  temperature: string;
  temperatureShort: string;
  speed: string;
  precipitation: string;
  distance: string;
  elevation: string;
}

/**
 * Composable for unit conversion based on user preferences
 */
export function useUnitConversion(unit?: () => TemperatureUnit) {
  const settingsStore = unit ? null : useSettingsStore();
  const selectedUnit = unit ?? (() => settingsStore!.temperatureUnit);

  // Computed boolean for quick checks
  const isMetric = computed(() => selectedUnit() === "Metric");
  const isImperial = computed(() => selectedUnit() === "Imperial");

  // Unit labels based on current setting
  const labels = computed<UnitLabels>(() => {
    if (isMetric.value) {
      return {
        temperature: "°C",
        temperatureShort: "°",
        speed: "kmh",
        precipitation: "mm",
        distance: "km",
        elevation: "m",
      };
    }
    return {
      temperature: "°F",
      temperatureShort: "°",
      speed: "mph",
      precipitation: "in",
      distance: "mi",
      elevation: "ft",
    };
  });

  // =========================================================================
  // Temperature Conversion
  // =========================================================================

  /**
   * Convert temperature from Imperial (°F) to user's preferred unit
   * @param tempF - Temperature in Fahrenheit
   * @param round - Whether to round to nearest integer (default: true)
   */
  function convertTemperature(
    tempF: number | null | undefined,
    round: boolean = true,
  ): number | null {
    if (tempF === null || tempF === undefined || tempF === -999 || isNaN(tempF))
      return null;

    if (isMetric.value) {
      const celsius = fahrenheitToCelsius(tempF);
      return round ? Math.round(celsius) : roundToPlaces(celsius, 1);
    }
    return round ? Math.round(tempF) : roundToPlaces(tempF, 1);
  }

  /**
   * Format temperature with unit symbol
   */
  function formatTemperature(
    tempF: number | null | undefined,
    showUnit: boolean = true,
  ): string {
    const converted = convertTemperature(tempF);
    if (converted === null) return "--";
    return showUnit
      ? `${converted}${labels.value.temperature}`
      : `${converted}`;
  }

  // =========================================================================
  // Wind Speed Conversion
  // =========================================================================

  /**
   * Convert wind speed from Imperial (mph) to user's preferred unit
   * @param speedMph - Speed in miles per hour
   */
  function convertSpeed(speedMph: number | null | undefined): number | null {
    if (
      speedMph === null ||
      speedMph === undefined ||
      speedMph === -999 ||
      isNaN(speedMph)
    )
      return null;

    if (isMetric.value) {
      return milesPerHourToKilometersPerHour(speedMph);
    }
    return Math.round(speedMph);
  }

  /**
   * Format wind speed with unit
   */
  function formatSpeed(speedMph: number | null | undefined): string {
    const converted = convertSpeed(speedMph);
    if (converted === null) return "--";
    return `${converted} ${labels.value.speed}`;
  }

  // =========================================================================
  // Precipitation Conversion
  // =========================================================================

  /**
   * Convert precipitation from Imperial (inches) to user's preferred unit
   * @param inches - Precipitation in inches
   * @param decimals - Number of decimal places (default: 2)
   */
  function convertPrecipitation(
    inches: number | null | undefined,
    decimals: number = 2,
  ): number | null {
    if (
      inches === null ||
      inches === undefined ||
      inches === -999 ||
      isNaN(inches)
    )
      return null;

    if (isMetric.value) {
      return roundToPlaces(inchesToMillimeters(inches), decimals);
    }
    return roundToPlaces(inches, decimals);
  }

  /**
   * Format precipitation with unit
   */
  function formatPrecipitation(
    inches: number | null | undefined,
    decimals: number = 2,
  ): string {
    const converted = convertPrecipitation(inches, decimals);
    if (converted === null) return "--";
    return `${converted.toFixed(decimals)} ${labels.value.precipitation}`;
  }

  // =========================================================================
  // Distance Conversion
  // =========================================================================

  /**
   * Convert distance from Imperial (miles) to user's preferred unit
   * @param miles - Distance in miles
   */
  function convertDistance(miles: number | null | undefined): number | null {
    if (miles === null || miles === undefined || miles === -999 || isNaN(miles))
      return null;

    if (isMetric.value) {
      return roundToPlaces(milesToKilometers(miles), 1);
    }
    return roundToPlaces(miles, 1);
  }

  /**
   * Format distance with unit
   */
  function formatDistance(miles: number | null | undefined): string {
    const converted = convertDistance(miles);
    if (converted === null) return "--";
    return `${converted} ${labels.value.distance}`;
  }

  // =========================================================================
  // Elevation Conversion
  // =========================================================================

  /**
   * Convert elevation from Imperial (feet) to user's preferred unit
   * @param feet - Elevation in feet
   */
  function convertElevation(feet: number | null | undefined): number | null {
    if (feet === null || feet === undefined || feet === -999 || isNaN(feet))
      return null;

    if (isMetric.value) {
      return Math.round(feetToMeters(feet));
    }
    return Math.round(feet);
  }

  /**
   * Format elevation with unit
   */
  function formatElevation(feet: number | null | undefined): string {
    const converted = convertElevation(feet);
    if (converted === null) return "--";
    return `${converted} ${labels.value.elevation}`;
  }

  return {
    // Reactive state
    isMetric,
    isImperial,
    labels,

    // Temperature
    convertTemperature,
    formatTemperature,

    // Speed
    convertSpeed,
    formatSpeed,

    // Precipitation
    convertPrecipitation,
    formatPrecipitation,

    // Distance
    convertDistance,
    formatDistance,

    // Elevation
    convertElevation,
    formatElevation,
  };
}
